import { useCallback, useEffect, useRef, useState } from 'react';
import { cn, List, RadioButton, RawHtml } from '@divlab/divanui';

import LayoutMain from '@Layouts/LayoutMain';
import * as Cart from '@Queries/Cart';
import * as Meta from '@Queries/Meta';
import * as ModalWindows from '@Stores/ModalWindows';
import * as HeaderStore from '@Stores/Header';
import CartBlock from '@Components/CartBlock';
import ShippingCostCalculator from '@Components/ShippingCostCalculator';
import Img from '@UI/Img';
import { useLayout } from '@Queries/useLayout';
import SmartRender from '@Components/SmartRender';
import usePage from '@Queries/usePage';
import useTranslation from '@Queries/useTranslation';
import PageTopBar from '@Components/PageTopBar';
import ShowroomsMap from '@Components/ShowroomsMap';
import Attention from './elements/Attention';
import PickupPoint from './elements/PickupPoint';
import ToAddress from './elements/ToAddress';
import FreeDeliveryBanner from './elements/FreeDeliveryBanner';
import WithTable from './elements/WithTable/WithTable';
import AcceptanceRules from './elements/AcceptanceRules/AcceptanceRules';
import Page from '@Components/Page';
import pickupPoint from './icons/pickupPoint.svg';
import toAddress from './icons/toAddress.svg';
import { useRequest } from '@Contexts/Request';
import styles from './PageDelivery.module.css';

import type { PageDeliveryData } from './typings';
import type { FC, HTMLAttributes } from 'react';
import './PageDelivery.raw.css';

export interface PaymentTypesIdsData {
  id: number;
  variants: string[];
}

export interface DeliveryData {
  address?: string;
  description?: string;
  id: number;
  name: string;
  type: 'pickupPoint' | 'toAddress';
  paymentTypesIds: PaymentTypesIdsData[];
}

const handleClickCity = () => {
  ModalWindows.open('RegionSelector', { context: { source: 'page-delivery' } });
};

export interface PageDeliveryProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  page: PageDeliveryData;
}

const PageDelivery: FC<PageDeliveryProps> = ({ page }) => {
  const {
    teaser,
    table,
    additionalCost,
    acceptanceRules,
    acceptanceProduct,
    deliveryTypes,
    needCalculator,
    assembling,
    assemblingOutside,
    calculatorTitle,
    map,
  } = page;
  const shortCart = Cart.useCartShortInfo();
  const cart = Cart.useCart();
  const productsCount = shortCart.data?.count || 0;
  const [checkedDelivery, setCheckedDelivery] = useState<DeliveryData>(null);
  const layout = useLayout();
  const meta = Meta.useMeta();
  const { t } = useTranslation();
  const { totalHeight } = HeaderStore.useSizes();
  const acceptanceRulesRef = useRef<HTMLDivElement>(null);
  const scrolled = useRef(false);
  const { isKaz } = useRequest();

  const handleChangeDeliveryType = useCallback((delivery: DeliveryData) => {
    setCheckedDelivery(delivery);
  }, []);

  const scrollToElem = useCallback(
    (elem: HTMLDivElement) => {
      if (!elem || !totalHeight || scrolled.current) return;

      const rect = elem.getBoundingClientRect();
      const top = window.scrollY + rect.top - totalHeight;

      window.scrollTo({ top, behavior: 'smooth' });
      scrolled.current = true;
    },
    [totalHeight],
  );

  useEffect(() => {
    if (window.location.hash === '#pravila-priema-mebeli' && acceptanceRulesRef.current) {
      scrollToElem(acceptanceRulesRef.current);
    }
  }, [scrollToElem]);

  useEffect(() => {
    setCheckedDelivery(deliveryTypes && deliveryTypes[0]);
  }, [deliveryTypes]);

  return (
    <LayoutMain>
      <Page>
        <PageTopBar
          title={page.meta.h1}
          breadcrumbs={page.breadcrumbs}
          navigation={page.pageMenu}
        />
        <div className={styles.page} data-testid='page-delivery'>
          {meta.data.region.isPec ? (
            <>
              <div className={styles.contentContainer}>
                <div className={styles.heading}>
                  {`${t('to-city')}\u00a0`}
                  <span className={styles.city} onClick={handleClickCity}>
                    {meta.data.region.name}
                  </span>
                </div>
                {checkedDelivery && (
                  <>
                    <List
                      className={styles.radioGroup}
                      items={deliveryTypes}
                      renderChild={(delivery: DeliveryData) => (
                        <div
                          className={cn(styles.radioItem, {
                            [styles.active]: checkedDelivery.type === delivery.type,
                          })}
                          onClick={() => {
                            handleChangeDeliveryType(delivery);
                          }}
                        >
                          <RadioButton
                            checked={checkedDelivery.type === delivery.type}
                            name='delivery'
                          />
                          <Img
                            className={cn(styles.radioIcon, {
                              [styles.toAddress]: delivery.type === 'toAddress',
                              [styles.pickupPoint]: delivery.type === 'pickupPoint',
                            })}
                            src={delivery.type === 'toAddress' ? toAddress : pickupPoint}
                            loading='eager'
                          />
                          <div className={styles.radioName}>{delivery.name}</div>
                        </div>
                      )}
                    />
                  </>
                )}
                <div className={styles.deliveryNote}>{t('pickup-note')}</div>
              </div>
              {productsCount > 0 && checkedDelivery && cart?.data && (
                <div className={styles.cartBlock}>
                  <div className={styles.contentContainer}>
                    <CartBlock
                      cart={cart.data}
                      type={checkedDelivery.type}
                      title={t('your-cart')}
                    />
                  </div>
                </div>
              )}
              <div className={styles.contentContainer}>
                {checkedDelivery?.type === 'pickupPoint' && (
                  <PickupPoint region={meta.data.region.name} />
                )}
                {checkedDelivery?.type === 'toAddress' && (
                  <ToAddress hasProductsInCart={productsCount > 0} />
                )}

                <div className={styles.deliveryText}>{t('conditions-note')}</div>
                <div className={styles.deliveryPhone}>
                  {layout.data.footer.contacts.items[0].text}
                </div>
              </div>

              {acceptanceProduct && (
                <div className={cn(styles.contentContainer, styles.attention)}>
                  <Attention text={acceptanceProduct} />
                </div>
              )}
            </>
          ) : (
            <>
              <div className={styles.contrastRow}>
                <div className={styles.deliveryContainer}>
                  <SmartRender countries={['BLR']} condition={!!teaser}>
                    <RawHtml html={teaser} className={styles.teaser} />
                  </SmartRender>
                  {page.freeDelivery && <FreeDeliveryBanner freeDelivery={page.freeDelivery} />}
                  {needCalculator && <ShippingCostCalculator title={calculatorTitle} />}

                  <SmartRender countries={['RUS', 'KAZ']} condition={!!teaser}>
                    <RawHtml
                      html={teaser}
                      className={cn(styles.teaser, {
                        [styles.teaserXL]: !needCalculator,
                      })}
                    />
                  </SmartRender>

                  {assembling && (
                    <div>
                      <div className={styles.infoTitle}>{assembling.title}</div>
                      <div className={styles.infoDescription}>
                        {assembling.text.map((descripton, index) => (
                          <div key={index} className={styles.description}>
                            {descripton}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {assemblingOutside && (
                    <div>
                      <div className={styles.infoTitle}>{assemblingOutside.title}</div>
                      <div className={styles.infoDescription}>{assemblingOutside.text}</div>
                    </div>
                  )}
                </div>
              </div>

              {table && (
                <WithTable
                  className={styles.contrastRow}
                  table={table}
                  additionalCost={additionalCost}
                />
              )}

              {acceptanceRules && (
                <AcceptanceRules
                  className={cn(styles.contrastRow, { [styles.transparent]: !table && isKaz })}
                  acceptanceRules={acceptanceRules}
                  acceptanceProduct={acceptanceProduct}
                  cnItem={styles.contrastItem}
                  ref={acceptanceRulesRef}
                />
              )}
              <SmartRender countries={['BLR']}>
                <div className={cn(styles.contentContainer)}>
                  <ShowroomsMap className={styles.deliveryMap} map={map} pickupPoint />
                </div>
              </SmartRender>
            </>
          )}
        </div>
      </Page>
    </LayoutMain>
  );
};

const PageDeliveryContainer: FC = () => {
  const { data } = usePage<PageDeliveryData>();

  return <PageDelivery page={data} />;
};

export default PageDeliveryContainer;
